import React, { useRef, useState, useEffect } from 'react';
import {
    Text,
    Box,
    Tooltip,
    Stack,
    Group,
    Progress,
} from '@mantine/core';
import { Card } from '@xplatform/design-system';
import {
    IconCloudUpload,
    IconFile,
    IconSettings,
    IconFilter,
    IconRefresh,
} from '@tabler/icons-react';
import { ProcessedResultsList, ExtractionOption, FilterPanel } from '@features/index';
import { useDoctypes, useExtraction } from '@src/hooks/useContext';
import { useAuth } from 'tecrmi_components/useAuth';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import './UploadSection.scss';
import type { IProcessedResultsFilterViewModel } from '@src/models';
import { useSelectedOrganisation } from 'xplatform_shell/useSelectedOrganisation';

const getInitialFilter = (userEmail?: string, userId?: string): IProcessedResultsFilterViewModel => {
    return {
        startDate: null,
        endDate: null,
        fileName: null,
        userId: userId,
        userEmail: userEmail,
        processId: null,
    };
};

interface UploadSectionProps {
    uploadedFile?: File;
    extractionOptions: ExtractionOptions;
    onFileUpload: (file: File) => void;
    onOptionsChange: (options: ExtractionOptions) => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
    uploadedFile,
    extractionOptions,
    onFileUpload,
    onOptionsChange,
}) => {
    const { isLoading: isDoctypesLoading } = useDoctypes();
    const { user } = useAuth();
    const { organisation } = useSelectedOrganisation();
    const {
        doctypeCode,
        isLoadingResults,
        isExtracting,
        uploadProgress,
        getProcessedResultsInfo,
    } = useExtraction();

    const [isPopoverOpen, setIsDrawerOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [currentFilter, setCurrentFilter] = useState<IProcessedResultsFilterViewModel>(getInitialFilter(user?.email, organisation?.userId));

    useEffect(() => {
        setIsDrawerOpen(false);
        const initialFilter = getInitialFilter(user?.email, organisation?.userId);
        setCurrentFilter(initialFilter);
        if (doctypeCode) {
            getProcessedResultsInfo(doctypeCode, initialFilter);
        }
    }, [doctypeCode, getProcessedResultsInfo, user, organisation]);

    const hasDoctype = !!doctypeCode;

    const handleFileUpload = async (file: File) => {
        onFileUpload(file);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) handleFileUpload(file);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const openFileDialog = () => {
        if (!hasDoctype || isDoctypesLoading) return;
        fileInputRef.current?.click();
    };

    const handleOpenFilterPanel = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        setIsFilterOpen(true);
    };

    const handleCloseFilterPanel = () => {
        setIsFilterOpen(false);
        setFilterAnchorEl(null);
    };

    const handleApplyFilter = (filter: IProcessedResultsFilterViewModel) => {
        if (!doctypeCode) return;
        setCurrentFilter(filter);
        getProcessedResultsInfo(doctypeCode, filter);
    };

    const handleRefresh = () => {
        if (!doctypeCode) return;
        getProcessedResultsInfo(doctypeCode, currentFilter);
    };

    const handleOpenPopover = () => {
        setIsDrawerOpen(true);
    };

    const handleClosePopover = () => {
        setIsDrawerOpen(false);
    };

    const handleSaveOptions = (options: ExtractionOptions) => {
        onOptionsChange(options);
    };

    const uploadDisabled = !hasDoctype || isDoctypesLoading;

    return (
        <Card className="upload-section">
            <Stack p="xs" gap="md">
                <Box
                    className={`upload-dropzone ${uploadDisabled ? 'disabled' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={openFileDialog}
                >
                    <IconCloudUpload
                        size={32}
                        className={`upload-icon ${uploadDisabled ? 'disabled' : ''}`}
                    />
                    <Text size="sm" mb="xs" c={uploadDisabled ? 'dimmed' : undefined}>
                        Drop files here or click to upload
                    </Text>
                    {!uploadDisabled && (
                        <Text size="xs" c="dimmed" mb="md">
                            Supported formats: PDF, JPG, JPEG, PNG, HEIC, ZIP
                        </Text>
                    )}
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                    onChange={handleFileSelect}
                    disabled={uploadDisabled}
                    style={{ display: 'none' }}
                />

                {uploadedFile && (
                    <Stack gap={6} className="uploaded-file-info" p="xs">
                        <Group justify="space-between" gap="xs" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                <IconFile size={18} className="icon-primary" style={{ flexShrink: 0 }} />
                                <Text size="sm" truncate="end">
                                    {uploadedFile.name}
                                </Text>
                            </Group>
                            <IconSettings
                                size={20}
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={handleOpenPopover}
                            />
                        </Group>
                        {isExtracting && (
                            <Stack gap={2}>
                                <Progress
                                    value={uploadProgress}
                                    size="sm"
                                    radius="xl"
                                    animated={uploadProgress < 100}
                                    striped={uploadProgress < 100}
                                />
                                <Text size="xs" c="dimmed" ta="right">
                                    {uploadProgress < 100
                                        ? `Uploading ${uploadProgress}%`
                                        : 'Upload complete'}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}

                {hasDoctype && (
                    <>
                        <Group justify="space-between" pos="relative">
                            <Box style={{ flex: 1 }} />
                            <Text size="sm" fw={600}>
                                Processed File(s)
                            </Text>
                            <Group gap="xs" style={{ flex: 1 }} justify="flex-end">
                                <Tooltip key="refresh" label="Refresh results" position="top">
                                    <span>
                                        <IconRefresh
                                            size={20}
                                            className="icon-primary"
                                            style={{ cursor: isLoadingResults ? 'not-allowed' : 'pointer', opacity: isLoadingResults ? 0.5 : 1 }}
                                            onClick={!isLoadingResults ? handleRefresh : undefined}
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip key="filter" label="Filter results" position="top">
                                    <span
                                        onClick={handleOpenFilterPanel}
                                        role="button"
                                        tabIndex={0}
                                        style={{ display: 'inline-flex', cursor: 'pointer' }}
                                    >
                                        <IconFilter
                                            size={20}
                                            className="icon-primary"
                                        />
                                    </span>
                                </Tooltip>
                            </Group>
                        </Group>
                    </>
                )}

                {hasDoctype && (
                    <ProcessedResultsList />
                )}
            </Stack>

            <FilterPanel
                anchorEl={filterAnchorEl}
                open={isFilterOpen}
                onClose={handleCloseFilterPanel}
                onApplyFilter={handleApplyFilter}
                initialFilter={currentFilter}
            />

            <ExtractionOption
                open={isPopoverOpen}
                onClose={handleClosePopover}
                onSave={handleSaveOptions}
                initialOptions={extractionOptions}
                uploadedFile={uploadedFile}
                doctypeCode={doctypeCode}
            />
        </Card>
    );
};

export default UploadSection;
